import { getContentfulClient } from './client';
import {
  formatMonthYear,
  parseContentfulDate,
  resolveAssetUrl,
  unwrapField,
  unwrapLinkField,
} from './utils';

const CONTENT_TYPE_PUBLICATIONS = 'publications';
const CONTENT_TYPE_THESIS = 'thesis';

const THESIS_TYPE_LABEL = {
  PhD: 'Ph.D.',
  SM: 'M.S.',
  MEng: 'M.Eng.',
};

async function fetchAllEntries(client, contentType, orderFieldId) {
  const limit = 100;
  let skip = 0;
  const items = [];
  const assetsById = new Map();
  const order = `-fields.${orderFieldId}`;

  for (;;) {
    const response = await client.getEntries({
      content_type: contentType,
      limit,
      skip,
      order,
      include: 10,
    });
    const batch = response.items || [];
    items.push(...batch);
    (response.includes?.Asset || []).forEach((asset) => {
      if (asset?.sys?.id) {
        assetsById.set(asset.sys.id, asset);
      }
    });
    if (batch.length < limit) {
      break;
    }
    skip += limit;
  }
  return { items, assetsById };
}

function mapPublicationEntry(entry) {
  const f = entry.fields;
  const dateRaw = unwrapField(f.dateOfPublication);
  const date = parseContentfulDate(dateRaw);
  const year =
    date && !Number.isNaN(date.getTime())
      ? date.getFullYear()
      : new Date().getFullYear();

  const type = unwrapField(f.type) || '';
  const publisher = unwrapField(f.nameOfPublisher) || '';
  const location = unwrapField(f.locationInformation);
  const venue = [type, publisher, location].filter(Boolean).join(' · ');

  return {
    id: entry.sys.id,
    title: unwrapField(f.publicationTitle) || '',
    authors: unwrapField(f.publicationAuthors) || '',
    venue,
    date: formatMonthYear(date),
    url: unwrapField(f.urlToThePublication) || '',
    sortKey: date && !Number.isNaN(date.getTime()) ? date.getTime() : 0,
    year,
  };
}

function mapThesisEntry(entry, assetsById) {
  const f = entry.fields;
  const dateRaw = unwrapField(f.dateOfThesis);
  const date = parseContentfulDate(dateRaw);
  const yearLabel = unwrapField(f.yearOfThesis);
  let year =
    date && !Number.isNaN(date.getTime())
      ? date.getFullYear()
      : parseInt(String(yearLabel), 10);
  if (Number.isNaN(year)) {
    year = new Date().getFullYear();
  }

  const thesisType = unwrapField(f.thesisType) || '';
  const typeLabel = THESIS_TYPE_LABEL[thesisType] || thesisType;
  const department = unwrapField(f.department) || '';
  const university = unwrapField(f.university) || '';
  const degree = `${typeLabel} Thesis, ${department}, ${university}`;

  const dateDisplay =
    formatMonthYear(date) || (yearLabel != null ? String(yearLabel) : '');

  return {
    id: entry.sys.id,
    title: unwrapField(f.thesisTitle) || '',
    author: unwrapField(f.thesisAuthor) || '',
    degree,
    date: dateDisplay,
    url: resolveAssetUrl(unwrapLinkField(f.thesisPdf), assetsById) || '',
    sortKey:
      date && !Number.isNaN(date.getTime())
        ? date.getTime()
        : year * 10000,
    year,
  };
}

function mergeByYear(publicationRows, thesisRows) {
  const map = new Map();

  const ensureYear = (y) => {
    if (!map.has(y)) {
      map.set(y, { year: y, publications: [], theses: [] });
    }
    return map.get(y);
  };

  const sortedPubs = [...publicationRows].sort((a, b) => b.sortKey - a.sortKey);
  const sortedTheses = [...thesisRows].sort((a, b) => b.sortKey - a.sortKey);

  sortedPubs.forEach((row) => {
    ensureYear(row.year).publications.push({
      id: row.id,
      title: row.title,
      authors: row.authors,
      venue: row.venue,
      date: row.date,
      url: row.url,
    });
  });

  sortedTheses.forEach((row) => {
    ensureYear(row.year).theses.push({
      id: row.id,
      title: row.title,
      author: row.author,
      degree: row.degree,
      date: row.date,
      url: row.url,
    });
  });

  return Array.from(map.values()).sort((a, b) => b.year - a.year);
}

export async function fetchPublicationsPageData() {
  const client = getContentfulClient();
  if (!client) {
    throw new Error('Contentful is not configured');
  }

  const [pubResponse, thesisResponse] = await Promise.all([
    fetchAllEntries(client, CONTENT_TYPE_PUBLICATIONS, 'dateOfPublication'),
    fetchAllEntries(client, CONTENT_TYPE_THESIS, 'dateOfThesis'),
  ]);

  const publicationRows = pubResponse.items.map(mapPublicationEntry);
  const thesisRows = thesisResponse.items.map((entry) =>
    mapThesisEntry(entry, thesisResponse.assetsById)
  );

  return mergeByYear(publicationRows, thesisRows);
}
